import React from 'react';
import { injectIntl, FormattedMessage, InjectedIntl } from 'react-intl';

import { logger } from '../../lib/utils/logger/logger';
import { VerificationService, StudentPersonalInfoViewModel, EmailLoopViewModel } from '../../lib/types/types';
import { VerificationStepsEnum } from '../../lib/types/runtimeTypes';
import { getSafe } from '../../lib/utils/objects';
import { getLogoUrl } from '../../lib/ProgramTheme/programThemeGetters';

import { LogoComponent } from '../LogoComponent/LogoComponent';
import { populateViewModelFromQueryParams } from '../../lib/utils/stepComponentHelpers/stepComponentHelpers';
import { usePollingInterval } from '../../lib/utils/usePollingInterval/usePollingInterval';


const EMAIL_LOOP_POLL_DURATION = 10000;

interface StepEmailLoopComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

export const StepEmailLoopComponent = ({ intl, verificationService }: StepEmailLoopComponentProps) => {
    const programId = verificationService.programId;
    const verificationId = verificationService.verificationResponse.verificationId;
    const viewModel = verificationService.viewModel as EmailLoopViewModel;
    const previousViewModel = (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
    const previousVerificationResponse = verificationService.previousVerificationResponse || undefined;

    const hasLogo = getLogoUrl(verificationService.programTheme) ? true : false;
    const logo = hasLogo ? (
        <LogoComponent verificationService={verificationService} />
    ) : (
        <img
            className="sid-l-lead-image"
            alt="design element"
            src="https://s3.amazonaws.com/com.sheerid.resources/common/images/2018/icons/doc-pending.svg"
        />
    );
    const email = getSafe(() => previousViewModel.email);

    const submitForm = () => {
        logger.info('StepEmailLoopComponent submitting form');
        verificationService.submitStep(VerificationStepsEnum.emailLoop, viewModel, verificationService.verificationResponse);
    };

    React.useEffect(() => {
        populateViewModelFromQueryParams(verificationService);
    }, []);

    React.useEffect(() => {
        if (viewModel.emailToken) {
            submitForm();
        }
    }, [viewModel.emailToken]);

    usePollingInterval(() => {
        verificationService.fetchExistingVerificationRequest(programId, verificationId, previousVerificationResponse, previousViewModel, false);
    }, EMAIL_LOOP_POLL_DURATION);

    return (
        <div id="sid-step-email-loop" className="sid-l-container">
            <div className="sid-header sid-l-horz-center">
                <div className="sid-l-horz-center">
                    {logo}
                </div>
                <div className="sid-header__title sid-l-horz-center sid-l-space-top-md">
                    <FormattedMessage id="step.emailLoop.title" defaultMessage="Check your email" />
                </div>
                <div className="sid-header__subtitle-one">
                    { email ? (
                        <FormattedMessage id="step.emailLoop.subtitleWithEmail"
                            defaultMessage="An email has been sent to {email} with a personalized link to complete the verification process."
                            values={{ email }}
                        />
                    ) : (
                        <FormattedMessage id="step.emailLoop.subtitleWithoutEmail"
                            defaultMessage="An email has been sent to your email account with a personalized link to complete the verification process."
                        />
                    )}
                </div>
                <div className="sid-l-space-top-md" />
                <div className="sid-header__subtitle-two sid-h-small-text sid-l-space-top-md">
                    <FormattedMessage
                        id="step.emailLoop.subtitle2"
                        defaultMessage="Please check for an email from us (verify@sheerid.com) for all the details."
                    />
                </div>
                <div className="sid-l-space-top-xl" />
            </div>
        </div>
    );
};

export const StepEmailLoopComponentComponent = injectIntl(StepEmailLoopComponent);
